import type { ChartConfiguration } from '@/stores/type/chartConfiguration.type'
import type { DatasetType } from '@/views/calculations/type/resultView.type'
import { ensureResultChartRegistered } from '@/views/calculations/utils/resultChart/registration'
import { downloadBlob, dataUrlToBlob, fileSafe } from '@/utils/fileDownload'
import {
  buildReportCanvas,
  type ChartExportReport
} from '@/views/calculations/utils/export/reportCanvas'
import { createPdfBlob } from '@/views/calculations/utils/export/pdf'
import { createWordBlob } from '@/views/calculations/utils/export/docx'
import {
  buildResultChartScales,
  buildResultChartDataLabels
} from '@/views/calculations/utils/resultChartOptions'
import { Chart } from 'chart.js'
import type { ChartDataset } from 'chart.js'

export type ChartExportFileType = 'png' | 'jpg' | 'pdf' | 'docx' | 'xlsx'

export const CHART_EXPORT_FILE_TYPES: { value: ChartExportFileType; title: string }[] = [
  { value: 'png', title: 'PNG image' },
  { value: 'jpg', title: 'JPG image' },
  { value: 'pdf', title: 'PDF document' },
  { value: 'docx', title: 'Word document' },
  { value: 'xlsx', title: 'Excel document' }
]

export type { ChartExportReport } // re-export so external imports keep working

type RenderChartImageParams = {
  config: ChartConfiguration
  labels: string[]
  datasets: DatasetType[]
}

export const renderChartImageDataUrl = ({ config, labels, datasets }: RenderChartImageParams) => {
  ensureResultChartRegistered()

  const canvas = document.createElement('canvas')
  canvas.width = 1200
  canvas.height = 620

  const chart = new Chart(canvas, {
    type: 'bar',
    data: {
      labels,
      datasets: datasets as ChartDataset<'bar'>[]
    },
    options: {
      animation: false,
      responsive: false,
      maintainAspectRatio: false,
      layout: {
        padding: { top: 60 }
      },
      plugins: {
        legend: {
          position: 'bottom'
        },
        tooltip: {
          enabled: false
        },
        datalabels: buildResultChartDataLabels({
          getDatasets: () => datasets,
          displayMode: () => config.display_mode ?? 'material_stack'
        })
      },
      scales: buildResultChartScales(config)
    }
  })

  chart.update()
  const imageDataUrl = canvas.toDataURL('image/png')
  chart.destroy()
  return imageDataUrl
}

export const downloadChartReport = async (
  report: ChartExportReport,
  fileType: ChartExportFileType
) => {
  const filename = `${fileSafe(report.title)}-${new Date()
    .toISOString()
    .slice(0, 16)
    .replace(/[:T]/g, '-')}`

  if (fileType === 'png' || fileType === 'jpg') {
    const reportDataUrl = await buildReportCanvas(report, fileType)
    if (!reportDataUrl) return
    downloadBlob(dataUrlToBlob(reportDataUrl), `${filename}.${fileType}`)
    return
  }

  if (fileType === 'pdf') {
    const reportDataUrl = await buildReportCanvas(report, 'jpg')
    if (!reportDataUrl) return
    downloadBlob(await createPdfBlob(reportDataUrl), `${filename}.pdf`)
    return
  }

  if (fileType === 'docx') {
    const wordBlob = await createWordBlob(report)
    if (!wordBlob) return
    downloadBlob(wordBlob, `${filename}.docx`)
  }
}
